import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

import { getTooltip } from './bar-graph-multi-tooltip';
import {
  getColorOptions,
  getDataZoomOptions,
  getGridOptions,
  getLegendOptions,
  getTitleOptions,
  getXAxisOptions,
  getYAxisOptions
} from './bar-graph-multi-options';

import type {
  BarGraphMulti,
  BarGraphMultiDataItem
} from '../types';
import type {
  ContentPerformanceDataType,
  ContentPerformanceKpi
} from 'modules/reporting/components/content-performance/types';

export const DEBOUNCE_DELAY = 250;

export const getFormattedDate = (date: string): string => (
  moment(date || '').format('YYYY-MM-DD')
);

export const getDateRange = (
  dates: Array<string>
): Array<string> => {
  const start = moment(dates[0]);
  const end = moment(dates[dates.length - 1]);
  const interim = start.clone();
  const dateRange: Array<string> = [];

  while (end.isSameOrAfter(interim, 'month')) {
    dateRange.push(interim.format('MMMM YYYY'));
    interim.add(1, 'month');
  }

  return dateRange;
};

export const getDates = (data: Array<BarGraphMultiDataItem>): Array<string> => {
  const dates = data
    .filter(item => !isNil(item.date))
    .map(item => getFormattedDate(item.date));

  return uniq(dates).sort();
};

export const getDataTypes = (
  data: Array<BarGraphMultiDataItem>,
  kpi: ContentPerformanceKpi
): Array<ContentPerformanceDataType> => {
  const dataTypes = data
    .filter(item => !isNil(item.metrics[kpi]))
    .map(item => item.type as ContentPerformanceDataType);

  return uniq(dataTypes);
};

export const getDataDimensions = (
  dataTypes: Array<ContentPerformanceDataType>
): Array<string> => (
  ['date'].concat(dataTypes)
);

export const getDataSource = (
  data: Array<BarGraphMultiDataItem>,
  dates: Array<string>,
  kpi: ContentPerformanceKpi
): Array<BarGraphMultiDataItem> => (
  dates.reduce<Array<BarGraphMultiDataItem>>((acc, date) => {
    const matchedByDate = data.filter(item => getFormattedDate(item.date) === date);
    const values = {};

    matchedByDate.forEach(item => {
      values[date] = { ...values[date] };
      values[date][String(item.type)] = values[date][String(item.type)] || 0;
      values[date][String(item.type)] += item.metrics[kpi] || 0;
    });

    const results = Object.keys(values).map(key => ({
      kpi,
      date: key,
      ...values[key]
    }));

    return acc.concat(results);
  }, [])
);

export const getData = (
  data: Array<BarGraphMultiDataItem>,
  kpi: ContentPerformanceKpi
): Partial<BarGraphMulti> => {
  if (isEmpty(data)) { // `isEmpty` also covers `null`
    return {};
  }

  const dates = getDates(data);
  const dateRange = getDateRange(dates);
  const dataTypes = getDataTypes(data, kpi);

  return {
    grid: getGridOptions(),
    title: getTitleOptions(dateRange),
    color: getColorOptions(dataTypes),
    dataZoom: getDataZoomOptions(),
    legend: getLegendOptions(),
    xAxis: getXAxisOptions(),
    yAxis: getYAxisOptions(kpi),
    tooltip: getTooltip(),
    series: dataTypes.map(() => ({
      type: 'bar',
      stack: 'data',
      cursor: 'auto'
    })),
    dataset: {
      dimensions: getDataDimensions(dataTypes),
      source: getDataSource(data, dates, kpi)
    }
  };
};
